import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { MarketFormModal } from 'src/components/market/market-form/MarketFormModal'
import type {
  MarketFormMode,
  MarketFormValues,
} from 'src/components/market/market-form/MarketForm.interface'
import { MarketsTable } from 'src/components/market/markets-table/MarketsTable'
import { MarketsTableToolbar } from 'src/components/market/markets-table/MarketsTableToolbar'
import { Button } from 'src/components/shared/Button'
import { AdminCreateMarket, AdminFetchMarkets, AdminRenameMarket } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { Market } from 'src/interfaces/market'
import { getMarketSaving } from 'src/selectors/market'
import { GSIcon } from 'src/components/shared/GSIcon'

interface ModalState {
  mode: MarketFormMode
  market: Market | null
}

const CustomerMarketsPageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const [searchQuery, setSearchQuery] = useState<string>('')
  // Bumped by the Refresh button and by a successful create or rename — included in the fetch
  // effect's dependency array below so any of them re-triggers the request.
  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [modalState, setModalState] = useState<ModalState | null>(null)

  const { isAuthenticated } = useAuthSession()
  const isSaving = useSelector(getMarketSaving)
  // Add and Edit (rename) both call `markets_manage` endpoints.
  const canManage = useHasPermission(InternalPermission.MarketsManage)

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // The single fetch effect: the endpoint takes no search or pagination params, so this only
  // reruns on a customer change, an auth change, or a manual refresh — not on the search term.
  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchMarkets.request({ partnerId: customerId }))
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, refreshToken])

  const handleOpenCreate = useCallback(() => {
    setModalState({ mode: 'create', market: null })
  }, [])

  const handleOpenEdit = useCallback((market: Market) => {
    setModalState({ mode: 'edit', market })
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalState(null)
  }, [])

  const handleSubmit = useCallback(
    async (values: MarketFormValues) => {
      if (!modalState || !customerId) {
        return
      }

      const title = values.title.trim()

      if (modalState.mode === 'edit') {
        if (!modalState.market) {
          return
        }

        // Only the title is sent — the backend discards anything else. See the design's §2.2.
        const renamed = await asyncDispatchWithErrorToast<Market>(
          AdminRenameMarket.request({ marketId: modalState.market.id, title }),
        )

        if (!renamed) {
          return
        }

        snackbar?.enqueueSnackbar('Market updated', { variant: 'success' })
        setModalState(null)
        handleRefresh()

        return
      }

      const description = values.description.trim()
      const created = await asyncDispatchWithErrorToast<Market>(
        AdminCreateMarket.request({
          partnerId: customerId,
          title,
          description: description === '' ? undefined : description,
        }),
      )

      if (!created) {
        return
      }

      snackbar?.enqueueSnackbar('Market added', { variant: 'success' })
      setModalState(null)
      handleRefresh()
    },
    [modalState, customerId, asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  return (
    <Fragment>
      <div data-testid="admin-customer-markets-page" css={tw`min-w-0 flex-1 p-6`}>
        <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
          <div>
            <Typography variant="h2">Markets</Typography>
          </div>
          {canManage ? (
            <Button
              size="small"
              color="dark"
              leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
              onClick={handleOpenCreate}
              data-testid="markets-list-add-button"
            >
              Add market
            </Button>
          ) : null}
        </div>
        <MarketsTableToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
        />
        <MarketsTable
          searchQuery={searchQuery}
          canManage={canManage}
          onEditMarket={handleOpenEdit}
        />
      </div>
      <MarketFormModal
        isOpen={Boolean(modalState)}
        mode={modalState?.mode ?? 'create'}
        market={modalState?.market ?? null}
        isSaving={isSaving}
        onSubmit={handleSubmit}
        onClose={handleCloseModal}
      />
    </Fragment>
  )
}

export const CustomerMarketsPage = memo(CustomerMarketsPageComponent)
